import React, { useState, useMemo } from 'react';
import { FaTimes, FaCalendarAlt, FaUsers } from 'react-icons/fa';
import { CrmTask } from '../../sales/services/CrmTaskService';
import { LaneGroup, LaneTeam } from '../services/PlantafelService';
import { personalakteStyles as ps, colors, shadows } from '../../employees/styles/personalakteStyles';

interface ScheduleTaskModalProps {
  isOpen: boolean;
  task: CrmTask;
  laneGroups: LaneGroup[];
  days: { date: Date; dateStr: string; label: string }[];
  onClose: () => void;
  onSchedule: (taskId: number, teamId: number, dateStr: string, taskTitle: string, teamName: string) => Promise<void>;
}

export default function ScheduleTaskModal({
  isOpen,
  task,
  laneGroups,
  days,
  onClose,
  onSchedule,
}: ScheduleTaskModalProps): React.JSX.Element | null {
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>(days[0]?.dateStr || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const allTeams: LaneTeam[] = useMemo(() => {
    return laneGroups.flatMap(group => group.teams);
  }, [laneGroups]);

  const selectedTeam = useMemo(() => {
    return allTeams.find(t => t.id === selectedTeamId);
  }, [allTeams, selectedTeamId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!selectedTeamId) {
      setError('Bitte Team auswählen');
      return;
    }

    if (!selectedDate) {
      setError('Bitte Datum auswählen');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSchedule(
        task.id,
        selectedTeamId,
        selectedDate,
        task.title,
        selectedTeam?.name || 'Team'
      );
      onClose();
    } catch (err: any) {
      if (err?.response?.status !== 409) {
        setError(err?.message || 'Fehler beim Einplanen');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        style={{
          background: colors.bgCard,
          borderRadius: '12px',
          width: '100%',
          maxWidth: '480px',
          boxShadow: shadows.elevated,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: '16px',
              fontWeight: 600,
              color: colors.navy,
            }}
          >
            Job einplanen
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              color: colors.textMuted,
            }}
          >
            <FaTimes size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ padding: '20px' }}>
            <div
              style={{
                background: colors.bgMuted,
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '20px',
              }}
            >
              <div
                style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: colors.navy,
                  marginBottom: '4px',
                }}
              >
                {task.title}
              </div>
              {task.partner_name && (
                <div style={{ fontSize: '12px', color: colors.textMuted }}>
                  {task.partner_name}
                </div>
              )}
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: colors.navy,
                  marginBottom: '8px',
                }}
              >
                <FaUsers size={14} />
                Team *
              </label>
              <select
                value={selectedTeamId || ''}
                onChange={(e) => setSelectedTeamId(e.target.value ? parseInt(e.target.value, 10) : null)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: '14px',
                  border: `1px solid ${colors.border}`,
                  borderRadius: '6px',
                  background: colors.bgCard,
                  cursor: 'pointer',
                }}
              >
                <option value="">Team auswählen...</option>
                {laneGroups.map(group => (
                  <optgroup key={group.workAreaId} label={group.workAreaName}>
                    {group.teams.map(team => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: colors.navy,
                  marginBottom: '8px',
                }}
              >
                <FaCalendarAlt size={14} />
                Datum *
              </label>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  gap: '4px',
                }}
              >
                {days.map(day => {
                  const isSelected = day.dateStr === selectedDate;
                  const isWeekend = day.date.getDay() === 0 || day.date.getDay() === 6;
                  return (
                    <button
                      key={day.dateStr}
                      type="button"
                      onClick={() => setSelectedDate(day.dateStr)}
                      style={{
                        padding: '8px 4px',
                        fontSize: '11px',
                        fontWeight: isSelected ? 600 : 400,
                        border: isSelected ? `2px solid ${colors.navy}` : `1px solid ${colors.border}`,
                        borderRadius: '10px',
                        background: isSelected ? colors.infoLight : isWeekend ? colors.bgMuted : colors.bgCard,
                        color: isSelected ? colors.navy : isWeekend ? colors.textLight : colors.navy,
                        cursor: 'pointer',
                        textAlign: 'center',
                      }}
                    >
                      <div>{day.label.split(' ')[0]}</div>
                      <div style={{ fontSize: '13px', fontWeight: 600 }}>
                        {day.date.getDate()}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div
              style={{
                background: colors.infoLight,
                borderRadius: '6px',
                padding: '10px 12px',
                fontSize: '12px',
                color: colors.navy,
              }}
            >
              <strong>Modus:</strong> Ganztags (gemäß Ops-Einstellungen)
            </div>

            {error && (
              <div
                style={{
                  marginTop: '12px',
                  padding: '10px 12px',
                  background: colors.dangerLight,
                  border: `1px solid ${colors.danger}`,
                  borderRadius: '6px',
                  fontSize: '13px',
                  color: colors.danger,
                }}
              >
                {error}
              </div>
            )}
          </div>

          <div
            style={{
              display: 'flex',
              gap: '12px',
              padding: '16px 20px',
              borderTop: `1px solid ${colors.border}`,
              justifyContent: 'flex-end',
            }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              style={{
                ...ps.buttonSecondary,
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
              }}
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !selectedTeamId || !selectedDate}
              style={{
                ...(isSubmitting || !selectedTeamId || !selectedDate ? ps.buttonDisabled : ps.buttonPrimary),
              }}
            >
              {isSubmitting ? 'Wird eingeplant...' : 'Einplanen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
